import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { ActionsButton, DataTable, SortableHeader } from '@/components/ui/data-table';
import { type Filter, SearchToolbar } from '@/components/ui/search-toolbar';
import { Segmented } from '@/components/ui/segmented';
import { useRouter } from '@/hooks/use-router';
import { formatDateOnly } from '@/lib/date';
import { meta } from '@/lib/meta';
import { usePermissions } from '@/services/auth';
import {
  useDynamicDelete,
  useDynamicList,
  useDynamicUpdate,
} from '@/services/dynamic-resource';
import { KanbanBoard } from './+components/kanban-board';
import { DeliveryBadge, STATUS_LABELS, StatusBadge } from './+components/status';

export const Route = createFileRoute('/_app/sales/')({
  head: () => meta({ label: 'Pedidos' }),
  component: RouteComponent,
});

function RouteComponent() {
  const { push } = useRouter();
  const { createPermissions } = usePermissions();
  const { mutateAsync: remove } = useDynamicDelete('sales');
  const { mutate: updateSale } = useDynamicUpdate('sales');

  const model = 'sales';
  const { canCreate, canRead, canDelete } = createPermissions(model as any);

  const [search, setSearch] = useState<Record<string, any>>({});
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('kanban');

  const useList = (params: any) => useDynamicList('sales', params);
  // Polling só no Kanban (drag-and-drop de status é onde importa ver mudanças de outros
  // usuários sem F5) — 5s dá uma sensação de "quase tempo real" sem virar chamada constante.
  const { data: kanbanData } = useDynamicList(
    'sales',
    { ...search, perPage: 200 },
    viewMode === 'kanban' ? { refetchInterval: 5000 } : undefined,
  );

  const handleAdd = () => push('/sales/create');
  const handleView = (id: string) => push(`/sales/${id}/view`);
  const handleRemove = async (id: string) => await remove(id);

  const handleStatusChange = (
    id: string,
    status: string,
    extra?: Record<string, string>,
  ) => {
    updateSale({ id, data: { status, ...extra } });
  };

  const filters: Filter<any>[] = [
    { type: 'text', label: 'Número', name: 'saleNumber' },
    {
      type: 'select',
      label: 'Status',
      name: 'status',
      options: Object.entries(STATUS_LABELS).map(([value, label]) => ({ label, value })),
    },
    { type: 'date', label: 'Entrega (de)', name: 'deliveryDateFrom' },
    { type: 'date', label: 'Entrega (até)', name: 'deliveryDateTo' },
  ];

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'saleNumber',
      header: ({ column }) => <SortableHeader column={column} label="Número" />,
      cell: ({ row }) => row.original.saleNumber ?? row.original.id.slice(0, 8),
    },
    {
      id: 'customer',
      header: 'Cliente',
      cell: ({ row }) => row.original.customer?.name ?? '—',
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <SortableHeader column={column} label="Status" />,
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'deliveryDate',
      header: ({ column }) => <SortableHeader column={column} label="Entrega" />,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span>{formatDateOnly(row.original.deliveryDate)}</span>
          <DeliveryBadge
            deliveryDate={row.original.deliveryDate}
            status={row.original.status}
          />
        </div>
      ),
    },
    {
      accessorKey: 'total',
      header: ({ column }) => <SortableHeader column={column} label="Total" />,
      cell: ({ row }) =>
        (row.original.total ?? 0).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }),
    },
    {
      id: 'actions',
      header: () => <div className="flex items-center justify-end">Ações</div>,
      enableHiding: false,
      cell: ({ row }) => (
        <ActionsButton
          id={row.original.id}
          edit={false}
          view={canRead}
          remove={canDelete}
          onView={handleView}
          onRemove={handleRemove}
        />
      ),
      meta: { sticky: 'right' },
    },
  ];

  return (
    <Content
      breadcrumbs={{
        title: 'Pedidos',
        icon: 'shopping-cart',
        action: canCreate && (
          <Button onClick={handleAdd} icon="plus">
            Novo Pedido
          </Button>
        ),
        items: [{ label: 'Listagem', active: true }],
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="flex-1">
          <SearchToolbar filters={filters} onSearch={setSearch} />
        </div>
        <Segmented
          className="h-11"
          value={viewMode}
          onChange={setViewMode}
          options={[
            { value: 'list', label: 'Lista', icon: 'list' },
            { value: 'kanban', label: 'Kanban', icon: 'kanban' },
          ]}
        />
      </div>

      {viewMode === 'list' ? (
        <DataTable
          columns={columns}
          useFetcher={useList as any}
          search={search}
          onRowDoubleClick={(row: any) => canRead && handleView(row.id)}
        />
      ) : (
        <KanbanBoard sales={kanbanData?.data ?? []} onStatusChange={handleStatusChange} />
      )}
    </Content>
  );
}
